import * as React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useUser, useClerk } from '@clerk/clerk-react';
import { ActionCreators as UndoActionCreators } from 'redux-undo';

import { clearNotifications, selectNotifications } from 'store/reducers/inputReducer.js';
import { selectScenarioId, selectScenarioName } from 'store/reducers/selectors/scenarioSelectors.js';
import { selectInExampleMode } from 'store/reducers/selectors/settingsSelectors.js';

import EditScenarioButton from 'components/domain/models/buttons/EditScenarioButton.jsx';
import { track, EVENTS } from 'utils/analytics.js';
import { CARD_TOKENS } from 'assets/theme/cardTokens.js';
import { ACCOUNT_BENEFITS, ctaPage } from 'components/common/user-management/accountOffer.js';

const stepZeroIconStyle = { width: '4rem', height: '4rem' };

function OptimizationCompletedDialog() {
  const notifications = useSelector(selectNotifications('optimization'));
  const scenarioName = useSelector(selectScenarioName);
  const scenarioId = useSelector(selectScenarioId);
  const theme = useTheme();
  const dispatch = useDispatch();
  const { isLoaded, isSignedIn } = useUser();
  const clerk = useClerk();
  const inExampleMode = useSelector(selectInExampleMode);
  /*
  Offer an account only to someone Clerk has confirmed is signed out, on a
  scenario they could save. While Clerk is still loading, isSignedIn is
  undefined, and a signed-in user would otherwise be asked to create an account.
  Examples can't be saved (the save buttons are hidden in example mode), so
  "save this plan" would be a promise the page can't keep.
  */
  const offerAccount = isLoaded === true && isSignedIn === false && !inExampleMode;

  const handleCancel = () => {
    dispatch(clearNotifications({ type: 'optimization' }));
    dispatch(UndoActionCreators.undo());
  };

  const handleOk = () => {
    dispatch(clearNotifications({ type: 'optimization' }));
  };

  const handleSignUpToSave = () => {
    track(EVENTS.CTA_CLICKED, {
      cta_id: 'post_solve_modal_signup',
      page: ctaPage(),
    });
    dispatch(clearNotifications({ type: 'optimization' }));
    if (clerk && clerk.openSignUp) {
      clerk.openSignUp({ mode: 'modal' });
    }
  };

  const isError = notifications && notifications[0] && notifications[0].type === 'error';
  const isSuccess = notifications && notifications[0] && notifications[0].type === 'success';

  return (
    <>
      {notifications && notifications.length > 0 && (
        <Dialog
          aria-labelledby="optimization-completed-dialog-title"
          sx={{ '& .MuiDialog-paper': { maxHeight: 520, maxWidth: 680, borderRadius: '12px' } }}
          maxWidth="md"
          keepMounted={false}
          open={notifications.length > 0}
          onClick={(event) => event.stopPropagation()}
        >
          <DialogTitle
            id="optimization-completed-dialog-title"
            style={{
              backgroundColor: isError ? theme.palette.warning.light : theme.palette.success.light,
              color: isError ? theme.palette.warning.main : theme.palette.success.dark,
              fontWeight: 700,
              paddingTop: '12px',
              paddingBottom: '12px',
            }}
          >
            <Grid container alignItems="center">
              <Grid item xs={12} sm={6}>
                {isError && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <WarningAmberIcon sx={{ height: '1.75rem', width: '1.75rem' }} />
                    <span>Optimization Report</span>
                  </Stack>
                )}
                {isSuccess && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckCircleOutlineIcon sx={{ height: '1.75rem', width: '1.75rem' }} />
                    <span>Optimization Complete</span>
                  </Stack>
                )}
              </Grid>
              <Grid item xs={12} sm={6} style={{ whiteSpace: 'nowrap' }}>
                <Stack direction="row" spacing={2} justifyContent="flex-end">
                  <Stack direction="column" style={{ fontSize: '0.75rem', lineHeight: '0.9rem' }}>
                    {isSuccess && (
                      <Stack direction="row" alignItems="center">
                        <span>Scenario Name:&nbsp;</span>
                        <span style={{ fontWeight: 600, overflow: 'hidden' }}>
                          {scenarioName}{' '}
                          {scenarioId != null && (
                            <EditScenarioButton
                              scenarioId={scenarioId}
                              buttonStyle={{ width: '1rem', height: '1rem', background: 'inherit' }}
                            />
                          )}
                        </span>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </DialogTitle>

          <DialogContent dividers sx={{ minHeight: '160px', py: 2.5 }}>
            {isSuccess && (
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <CheckCircleOutlineIcon color="success" style={{ ...stepZeroIconStyle, flexShrink: 0, marginTop: '4px' }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5, fontSize: '1.1rem' }}>
                    Routes optimized successfully!
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 1.5 }}>
                    {offerAccount
                      ? 'Your vehicle routes, stops, and schedules are ready. Create a free account to save this plan and come back to it from any device.'
                      : 'Your vehicle routes, stops, and schedules are ready to review on the map and in the results below.'}
                  </Typography>

                  {offerAccount && (
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: '8px',
                        backgroundColor: CARD_TOKENS.bg,
                        border: `1px solid ${CARD_TOKENS.border}`,
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: CARD_TOKENS.primary, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 0.75 }}>
                        With a free account
                      </Typography>
                      <Stack spacing={0.5}>
                        {ACCOUNT_BENEFITS.map((benefit) => (
                          <Stack key={benefit} direction="row" spacing={0.75} alignItems="center">
                            <CheckCircleOutlineIcon sx={{ fontSize: '0.95rem', color: CARD_TOKENS.success }} />
                            <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: 600, color: CARD_TOKENS.text }}>
                              {benefit}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Stack>
            )}

            {isError && (
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <WarningAmberIcon color="warning" style={{ ...stepZeroIconStyle, flexShrink: 0 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Optimization did not terminate successfully
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Please verify your vehicle and stop constraints, or adjust input parameters and try again.
                  </Typography>
                </Box>
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
            <Box>
              {isSuccess && (
                <Button
                  onClick={handleCancel}
                  size="small"
                  sx={{
                    color: 'text.secondary',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': { color: 'error.main', backgroundColor: 'error.extraLight' },
                  }}
                >
                  Clear results
                </Button>
              )}
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button
                onClick={handleOk}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: 'divider',
                  color: 'text.secondary',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '6px',
                  '&:hover': { borderColor: 'primary.main', backgroundColor: 'primary.extraLight' },
                }}
              >
                View Results
              </Button>

              {offerAccount && isSuccess && (
                <Button
                  onClick={handleSignUpToSave}
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: CARD_TOKENS.primary,
                    color: '#ffffff',
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: '6px',
                    px: 2,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#5524B8',
                      boxShadow: `0 4px 12px ${CARD_TOKENS.shadow}`,
                    },
                  }}
                >
                  Create Free Account to Save
                </Button>
              )}
            </Stack>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}

export default OptimizationCompletedDialog;
